import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { benachrichtigeModulAenderung } from '../hooks/useAktiveModule';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

const BRANCHEN_INFO: Record<string, { label: string; beschreibung: string; emoji: string }> = {
  produktion: { label: 'Produktion', emoji: '01', beschreibung: 'Fertigung, Stücklisten, Qualitätskontrolle — z.B. Möbelhaus, Metallbau' },
  handwerk:   { label: 'Handwerk',   emoji: '02', beschreibung: 'Projekte, Zeiterfassung, Materialentnahme — z.B. Elektriker, Installateur' },
  handel:     { label: 'Handel',     emoji: '03', beschreibung: 'Einkauf, Lager, Versand ohne Fertigung — z.B. B2B-Großhandel' },
  projektgeschaeft: { label: 'Projektgeschäft', emoji: '04', beschreibung: 'Projekte, Zeiterfassung, Abrechnung — z.B. Agentur, IT, Beratung' },
};

interface BranchePickerProps {
  onBrancheGewaehlt?: (branche: string) => void;
  authToken?: string;
}

const BranchePicker: React.FC<BranchePickerProps> = ({ onBrancheGewaehlt, authToken }) => {
  const [aktuellerBranche, setAktuellerBranche] = useState<string>('produktion');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [erfolg, setErfolg] = useState<string | null>(null);
  const headers = useMemo(
    () => (authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    [authToken]
  );

  useEffect(() => {
    axios.get(`${API_URL}/api/mandant/branche`, { headers })
      .then(r => setAktuellerBranche(r.data.branche))
      .finally(() => setLoading(false));
  }, [headers]);

  const handleSave = async (branche: string) => {
    setSaving(true);
    setErfolg(null);
    try {
      await axios.patch(`${API_URL}/api/mandant/branche`, { branche }, { headers });
      setAktuellerBranche(branche);
      setErfolg(`Branche auf "${BRANCHEN_INFO[branche]?.label}" gesetzt. Module wurden angepasst.`);
      benachrichtigeModulAenderung();
      onBrancheGewaehlt?.(branche);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ padding: 16 }}>Lade Branchen-Einstellungen…</div>;

  return (
    <div style={{ padding: 16 }}>
      <h3 style={{ marginTop: 0 }}>Branche wählen</h3>
      <p style={{ color: 'var(--ink-2)', marginBottom: 16 }}>
        Die Branche bestimmt welche Module und Status-Optionen für diesen Mandanten aktiv sind.
      </p>

      {erfolg && (
        <div style={{ background: 'var(--bone-2)', border: '1px solid var(--green-400)', padding: '10px 16px', borderRadius: 0, marginBottom: 16 }}>
          {erfolg}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        {Object.entries(BRANCHEN_INFO).map(([key, info]) => (
          <div
            key={key}
            onClick={() => !saving && handleSave(key)}
            style={{
              border: `2px solid ${aktuellerBranche === key ? 'var(--green-900)' : 'var(--bone-3)'}`,
              borderRadius: 0,
              padding: 16,
              cursor: saving ? 'wait' : 'pointer',
              background: aktuellerBranche === key ? 'var(--bone-2)' : 'var(--bone)',
              transition: 'border-color 0.15s, background 0.15s',
            }}
          >
            <div style={{ fontSize: 28, marginBottom: 8 }}>{info.emoji}</div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>{info.label}</div>
            <div style={{ fontSize: 13, color: 'var(--ink-2)' }}>{info.beschreibung}</div>
            {aktuellerBranche === key && (
              <div style={{ marginTop: 8, fontSize: 12, color: 'var(--green-900)', fontWeight: 500 }}>✓ Aktiv</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default BranchePicker;
